/** Design tokens editor — Phase B1 (ARCHITECTURE.md).
 *
 * SSOT для брендового вида трёх фронтов (web/TMA/mobile). Эта страница
 * пишет `app_settings.metadata->'design_tokens'` через PUT
 * `/admin/v1/design-tokens`, после чего:
 *   - web/TMA на следующий запрос инжектят новые CSS vars в `<head>`
 *   - mobile при перезапуске подхватывает через `AppTheme.fromTokens`
 *
 * Группы цветов структурированы по семантике, в каждой строке нативный
 * `<input type="color">` + текстовое поле HEX (синхронизированы). Шрифты,
 * радиусы, spacing — простые input'ы (raw редактирование на M1, color
 * picker появится в M2 если потребуется).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Palette, Save, RotateCcw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";

interface DesignTokensPayload {
  colors?: Record<string, string>;
  fonts?: Record<string, string>;
  radius?: Record<string, number>;
  spacing?: Record<string, number>;
  brand?: { currency_label?: string } & Record<string, unknown>;
}

interface ColorGroupDef {
  title: string;
  hint: string;
  keys: { key: string; label: string }[];
}

// Группы цветов отражают логику использования. Ключи — точно те же
// snake_case, что в backend JSONB (миграция 041, design_tokens_service.py).
const COLOR_GROUPS: ColorGroupDef[] = [
  {
    title: "Brand / Primary",
    hint: "Основной brand-цвет и CTA-кнопки (фон/hover/active). Меняй здесь чтобы перекрасить весь сайт + mobile.",
    keys: [
      { key: "brand", label: "Brand" },
      { key: "brand_light", label: "Brand light" },
      { key: "brand_dark", label: "Brand dark" },
      { key: "primary", label: "Primary" },
      { key: "primary_hover", label: "Primary hover" },
      { key: "primary_active", label: "Primary active" },
      { key: "primary_foreground", label: "Primary text" },
      { key: "primary_surface", label: "Primary surface" },
    ],
  },
  {
    title: "Surfaces / Neutrals",
    hint: "Фон страницы и карточек, текст, мутед-тона.",
    keys: [
      { key: "background", label: "Page background" },
      { key: "surface", label: "Card surface" },
      { key: "surface_2", label: "Surface 2" },
      { key: "surface_3", label: "Surface 3" },
      { key: "foreground", label: "Foreground (text)" },
      { key: "muted", label: "Muted" },
      { key: "muted_foreground", label: "Muted foreground" },
      { key: "hint", label: "Hint" },
      { key: "border", label: "Border" },
      { key: "border_strong", label: "Border strong" },
      { key: "border_focus", label: "Border focus" },
      { key: "divider", label: "Divider" },
    ],
  },
  {
    title: "Status",
    hint: "Стандартные toast / badge цвета. Не менять без причины — пользователи привыкли к зелёному=ок/красному=ошибка.",
    keys: [
      { key: "success", label: "Success" },
      { key: "success_light", label: "Success light" },
      { key: "warning", label: "Warning" },
      { key: "warning_light", label: "Warning light" },
      { key: "error", label: "Error" },
      { key: "error_light", label: "Error light" },
      { key: "info", label: "Info" },
      { key: "info_light", label: "Info light" },
    ],
  },
  {
    title: "Sale / Stars",
    hint: "Badge скидки на карточке товара + цвет звёзд рейтинга.",
    keys: [
      { key: "sale", label: "Sale badge" },
      { key: "sale_foreground", label: "Sale text" },
      { key: "star", label: "Star (rating)" },
    ],
  },
  {
    title: "Brand navy palette",
    hint: "50..950 шкала вокруг основного navy. Используется в редких кастомных стилях; обычно достаточно brand/primary групп выше.",
    keys: [
      { key: "navy_50", label: "Navy 50" },
      { key: "navy_100", label: "Navy 100" },
      { key: "navy_200", label: "Navy 200" },
      { key: "navy_300", label: "Navy 300" },
      { key: "navy_400", label: "Navy 400" },
      { key: "navy_500", label: "Navy 500" },
      { key: "navy_600", label: "Navy 600" },
      { key: "navy_700", label: "Navy 700" },
      { key: "navy_800", label: "Navy 800" },
      { key: "navy_900", label: "Navy 900" },
      { key: "navy_950", label: "Navy 950" },
    ],
  },
];

const RADIUS_KEYS = ["xs", "sm", "md", "lg", "xl", "2xl", "full"];
const SPACING_KEYS = ["card", "section", "page"];

export function DesignTokensPage(): JSX.Element {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["design-tokens"],
    queryFn: async () => {
      const { data } = await api.get<DesignTokensPayload>("/design-tokens");
      return data;
    },
  });

  const [draft, setDraft] = useState<DesignTokensPayload>({});

  useEffect(() => {
    if (data) setDraft(data);
  }, [data]);

  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(data ?? {}),
    [draft, data],
  );

  const save = useMutation({
    mutationFn: async () => {
      const { data } = await api.put<DesignTokensPayload>(
        "/design-tokens",
        draft,
      );
      return data;
    },
    onSuccess: (fresh) => {
      qc.setQueryData(["design-tokens"], fresh);
      toast.success("Design tokens сохранены. Web/TMA увидят изменения через ~60 сек кэша, mobile — после перезапуска.");
    },
    onError: (e: unknown) => {
      const msg =
        e instanceof Error ? e.message : "Не удалось сохранить design tokens";
      toast.error(msg);
    },
  });

  const setColor = (key: string, hex: string) =>
    setDraft((d) => ({ ...d, colors: { ...(d.colors ?? {}), [key]: hex } }));
  const setFont = (key: string, value: string) =>
    setDraft((d) => ({ ...d, fonts: { ...(d.fonts ?? {}), [key]: value } }));
  const setRadius = (key: string, value: number) =>
    setDraft((d) => ({ ...d, radius: { ...(d.radius ?? {}), [key]: value } }));
  const setSpacing = (key: string, value: number) =>
    setDraft((d) => ({ ...d, spacing: { ...(d.spacing ?? {}), [key]: value } }));
  const setBrandField = (key: string, value: string) =>
    setDraft((d) => ({ ...d, brand: { ...(d.brand ?? {}), [key]: value } }));

  if (isLoading) {
    return <Skeleton className="h-96" />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button asChild variant="ghost" size="sm">
            <Link to="/configuration">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Конфигурация
            </Link>
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            disabled={!dirty || save.isPending}
            onClick={() => setDraft(data ?? {})}
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            Откатить
          </Button>
          <Button
            size="sm"
            disabled={!dirty || save.isPending}
            onClick={() => save.mutate()}
          >
            <Save className="w-4 h-4 mr-1" />
            {save.isPending ? "Сохранение..." : "Сохранить"}
          </Button>
        </div>
      </div>

      <div>
        <h1 className="text-2xl font-heading flex items-center gap-2">
          <Palette className="w-6 h-6" />
          Design tokens (brand look)
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Единый источник правды для брендового вида web/TMA/mobile.
          Поменяй цвет здесь — все три фронта обновятся (web через ~60 сек
          ISR, mobile — после перезапуска приложения).
        </p>
      </div>

      {COLOR_GROUPS.map((group) => (
        <Card key={group.title}>
          <CardContent className="p-4 space-y-3">
            <div>
              <h2 className="font-medium">{group.title}</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                {group.hint}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {group.keys.map(({ key, label }) => (
                <ColorRow
                  key={key}
                  label={label}
                  fieldKey={key}
                  value={draft.colors?.[key] ?? ""}
                  onChange={(hex) => setColor(key, hex)}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      ))}

      <Card>
        <CardContent className="p-4 space-y-3">
          <div>
            <h2 className="font-medium">Шрифты</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              CSS font-family stacks. Первый item — brand шрифт (когда
              привезут лицензионные .ttf — пропишем сюда), дальше fallback.
            </p>
          </div>
          <FieldText
            label="Heading (заголовки)"
            value={draft.fonts?.heading ?? ""}
            onChange={(v) => setFont("heading", v)}
            placeholder="Nunito, system-ui, sans-serif"
          />
          <FieldText
            label="Body (основной текст)"
            value={draft.fonts?.body ?? ""}
            onChange={(v) => setFont("body", v)}
            placeholder="Onest, system-ui, sans-serif"
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4 space-y-3">
          <div>
            <h2 className="font-medium">Радиусы (px)</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Скругление углов карточек, кнопок, инпутов.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
            {RADIUS_KEYS.map((k) => (
              <FieldNumber
                key={k}
                label={k}
                value={draft.radius?.[k] ?? 0}
                onChange={(v) => setRadius(k, v)}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4 space-y-3">
          <div>
            <h2 className="font-medium">Spacing (px)</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Отступы внутри карточек, секций и страницы.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {SPACING_KEYS.map((k) => (
              <FieldNumber
                key={k}
                label={k}
                value={draft.spacing?.[k] ?? 0}
                onChange={(v) => setSpacing(k, v)}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4 space-y-3">
          <div>
            <h2 className="font-medium">Brand</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Нелокализуемые brand-параметры. Локализуемый текст
              (название, tagline, hero) — на странице «Контент сайта».
            </p>
          </div>
          <FieldText
            label="Currency label"
            value={draft.brand?.currency_label ?? ""}
            onChange={(v) => setBrandField("currency_label", v)}
            placeholder="сум"
          />
        </CardContent>
      </Card>
    </div>
  );
}

function ColorRow({
  label,
  fieldKey,
  value,
  onChange,
}: {
  label: string;
  fieldKey: string;
  value: string;
  onChange: (hex: string) => void;
}): JSX.Element {
  // <input type="color"> требует валидный #RRGGBB. Если в БД хранится
  // короткая или неполная строка — отображаем placeholder swatch.
  const normalized = /^#[0-9a-fA-F]{6}$/.test(value) ? value : "#000000";
  return (
    <div className="space-y-1.5">
      <Label className="text-xs" htmlFor={`color-${fieldKey}`}>
        {label}{" "}
        <code className="text-[10px] text-muted-foreground">{fieldKey}</code>
      </Label>
      <div className="flex items-center gap-2">
        <input
          id={`color-${fieldKey}`}
          type="color"
          value={normalized}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          className="h-9 w-12 cursor-pointer rounded border border-border bg-transparent p-0"
          aria-label={label}
        />
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          placeholder="#000000"
          className="font-mono uppercase text-xs"
        />
      </div>
    </div>
  );
}

function FieldText({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}): JSX.Element {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}

function FieldNumber({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}): JSX.Element {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Input
        type="number"
        min={0}
        value={value}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
      />
    </div>
  );
}
